import * as React from 'react';
import { isEqual } from 'vet-bones/utils';
import { usePortalFeatures } from 'vet-bones/utils/portal';

import { requestSuggestions } from 'src/actions/data-sources/action-creators/requestSuggestions';
import { requestVoiceboxSuggestions } from 'src/actions/data-sources/action-creators/requestVoiceboxSuggestions';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { Suggestion } from 'src/containers/ProjectMapping/types';
import {
  MappingSuggestion,
  ProjectResourceSuggestionsData,
} from 'src/containers/ProjectResourceSuggestionsDialog/projectResourceSuggestionsData';
import { getMappingSuggestions } from 'src/containers/ProjectResourceSuggestionsDialog/utils/getMappingSuggestions';
import { IgnoredSuggestion } from 'src/containers/ProjectResourceSuggestionsDialog/utils/getOrganizedSuggestionRecords';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectMappingsSelector } from 'src/selectors/projectMappings';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useFetchMappingSuggestions = () => {
  const dispatch = useReduxDispatch();
  const diagram = useDiagram();
  const connection = useDefaultPortalConnection();
  const { projectId } = useRouterParams();

  const portalFeatures = usePortalFeatures();
  const useVoiceboxSuggestions = Boolean(
    useIsVoiceboxEnabled() && portalFeatures.voiceboxSuggestions
  );

  const labelsByIri = useReduxSelector(
    projectLabelsByIriSelector(projectId),
    isEqual
  );
  const mappings = useReduxSelector(
    projectMappingsSelector(projectId),
    isEqual
  );

  const isMounted = useIsMounted();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const fetchMappingSuggestions = React.useCallback(
    async ({
      selectedClasses,
      selectedResources,
    }: Pick<
      ProjectResourceSuggestionsData,
      'selectedClasses' | 'selectedResources'
    >): Promise<{
      ignoredSuggestions: IgnoredSuggestion[] | null;
      mappingSuggestions: MappingSuggestion[] | null;
      rawSuggestions: Suggestion[] | null;
    }> => {
      if (!connection || !diagram) {
        return {
          ignoredSuggestions: null,
          mappingSuggestions: null,
          rawSuggestions: null,
        };
      }

      // Request mapping suggestions

      setIsLoading(true);
      const suggestions = useVoiceboxSuggestions
        ? await dispatch(
            requestVoiceboxSuggestions({
              classIris: selectedClasses,
              connection,
              projectId,
              resourceIds: selectedResources,
            })
          )
        : await dispatch(
            requestSuggestions({
              classIris: selectedClasses,
              connection,
              projectId,
              resourceIds: selectedResources,
            })
          );

      setIsLoading(false);
      if (!isMounted.current) {
        return {
          ignoredSuggestions: null,
          mappingSuggestions: null,
          rawSuggestions: null,
        };
      }

      if (!suggestions) {
        return {
          ignoredSuggestions: [],
          mappingSuggestions: null,
          rawSuggestions: null,
        };
      }

      const { ignoredSuggestions, mappingSuggestions } = getMappingSuggestions({
        labelsByIri,
        mappings,
        selectedClasses,
        suggestions,
      });
      if (!mappingSuggestions.length) {
        return {
          ignoredSuggestions,
          mappingSuggestions: null,
          rawSuggestions: suggestions,
        };
      }

      return {
        ignoredSuggestions: null,
        mappingSuggestions,
        rawSuggestions: suggestions,
      };
    },
    [
      connection,
      diagram,
      dispatch,
      isMounted,
      labelsByIri,
      mappings,
      projectId,
      useVoiceboxSuggestions,
    ]
  );

  return {
    fetchMappingSuggestions,
    isLoading,
    setIsLoading,
  };
};
